import React, { useState, useEffect, useCallback } from "react";
import { ShieldCheck, ListChecks, Gavel, Flag, TrendingUp, FileDown, Users, Network, Gauge, Bell, HelpCircle, ScrollText, Boxes, BarChart3, FileBadge, Radar } from "lucide-react";
import { Eyebrow, BASE } from "./e360Primitives";
import api from "../../lib/api";
import RaidRegister from "./RaidRegister";
import DecisionLog from "./DecisionLog";
import StageGates from "./StageGates";
import BenefitsTracker from "./BenefitsTracker";
import PortalAccessManager from "./PortalAccessManager";
import EvidenceGraph from "./EvidenceGraph";
import ProgrammeAssurance from "./ProgrammeAssurance";
import DeliveryAlerts from "./DeliveryAlerts";
import AssumptionRegister from "./AssumptionRegister";
import StandardsRegulatory from "./StandardsRegulatory";
import DigitalTwin from "./DigitalTwin";
import BenchmarkPanel from "./BenchmarkPanel";
import AssuranceMRM from "./AssuranceMRM";
import EvidenceMaturity from "./EvidenceMaturity";
import ModelRegister from "./ModelRegister";

const TABS = [
  { id: "assurance", label: "Assurance", icon: Gauge },
  { id: "alerts", label: "Early warning", icon: Bell },
  { id: "raid", label: "RAID register", icon: ListChecks },
  { id: "assumptions", label: "Assumptions", icon: HelpCircle },
  { id: "decisions", label: "Decision log", icon: Gavel },
  { id: "gates", label: "Stage-gates", icon: Flag },
  { id: "benefits", label: "Benefits realisation", icon: TrendingUp },
  { id: "evidence", label: "Evidence graph", icon: Network },
  { id: "standards", label: "Standards & regulatory", icon: ScrollText },
  { id: "mrm", label: "Governance (MRM)", icon: FileBadge },
  { id: "maturity", label: "Evidence maturity", icon: Radar },
  { id: "modelrisk", label: "Model register", icon: Boxes },
  { id: "twin", label: "Digital twin", icon: Boxes },
  { id: "benchmark", label: "Benchmark", icon: BarChart3 },
  { id: "access", label: "Client access", icon: Users },
];

export default function DeliveryHub({ clientId, focus }) {
  const [tab, setTab] = useState(focus?.tab || "assurance");
  const [alerts, setAlerts] = useState(null);
  const [tomStat, setTomStat] = useState(null);
  useEffect(() => { if (focus?.tab) setTab(focus.tab); }, [focus]);

  useEffect(() => {
    const onGoto = (e) => { const t = e?.detail; if (t) setTab(t); };
    window.addEventListener("e360-hub-goto-tab", onGoto);
    return () => window.removeEventListener("e360-hub-goto-tab", onGoto);
  }, []);

  const loadAlerts = useCallback(async () => {
    if (!clientId) return;
    try { const { data } = await api.get(`/admin/enterprise360/clients/${clientId}/alerts`); setAlerts(data); }
    catch { /* non-blocking */ }
  }, [clientId]);
  useEffect(() => { loadAlerts(); }, [loadAlerts]);

  const loadTomStat = useCallback(async () => {
    if (!clientId) return;
    try { const { data } = await api.get(`/admin/delivery-tom/engagements/${clientId}/promoted`); setTomStat(data); }
    catch { /* non-blocking */ }
  }, [clientId]);
  useEffect(() => { loadTomStat(); }, [loadTomStat, tab]);

  const active = alerts?.counts?.active || 0;
  const high = alerts?.counts?.high || 0;

  return (
    <div data-testid="e360-delivery-hub" className="border border-zinc-200 rounded-lg p-4 bg-white mb-6">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <Eyebrow><ShieldCheck size={13} className="inline mr-1" /> Delivery &amp; Assurance Hub</Eyebrow>
          <p className="text-[12px] text-zinc-500 -mt-1">The living delivery system — govern the engagement inside the platform, not in a static artefact.</p>
          {tomStat?.count > 0 && (
            <button data-testid="hub-tom-delivery-stat" onClick={() => setTab("raid")}
              title="Delivery TOM recommendations promoted into this engagement's RAID register — click to view"
              className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-violet-700 bg-violet-50 border border-violet-200 rounded-full px-2.5 py-1 hover:bg-violet-100 transition-colors">
              <ListChecks size={12} /> TOM: <b>{tomStat.count}</b> promoted → <b className="text-emerald-700">{tomStat.closed}</b> closed ({tomStat.pct_closed}%)
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a data-testid="hub-evidence-trail-btn" href={`${BASE}/api/admin/enterprise360/clients/${clientId}/evidence-trail.pdf`}
            className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.12em] uppercase text-[var(--e360-primary)] border border-[var(--e360-primary)] px-3 py-1.5 rounded hover:bg-[var(--e360-primary)]/5">
            <FileDown size={14} /> Evidence trail
          </a>
          <a data-testid="hub-status-pack-btn" href={`${BASE}/api/admin/enterprise360/clients/${clientId}/status-pack.pdf`}
            className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.12em] uppercase text-white bg-[var(--e360-primary)] px-3 py-1.5 rounded hover:opacity-90">
            <FileDown size={14} /> Delivery status pack
          </a>
        </div>
      </div>
      <div className="flex gap-2 mb-4 border-b border-zinc-200 flex-wrap">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`hub-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-[12px] tracking-[0.12em] uppercase border-b-2 -mb-px transition-colors ${tab === t.id ? "border-[var(--e360-primary)] text-black" : "border-transparent text-zinc-400 hover:text-zinc-600"}`}>
            <t.icon size={14} /> {t.label}
            {t.id === "alerts" && active > 0 && (
              <span data-testid="hub-alerts-badge" className={`ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white ${high > 0 ? "bg-red-500" : "bg-amber-500"}`}>{active}</span>
            )}
          </button>
        ))}
      </div>
      {tab === "assurance" && <ProgrammeAssurance clientId={clientId} />}
      {tab === "alerts" && <DeliveryAlerts clientId={clientId} data={alerts} reload={loadAlerts} />}
      {tab === "raid" && <RaidRegister clientId={clientId} />}
      {tab === "assumptions" && <AssumptionRegister clientId={clientId} />}
      {tab === "decisions" && <DecisionLog clientId={clientId} />}
      {tab === "gates" && <StageGates clientId={clientId} />}
      {tab === "benefits" && <BenefitsTracker clientId={clientId} />}
      {tab === "evidence" && <EvidenceGraph clientId={clientId} />}
      {tab === "standards" && <StandardsRegulatory clientId={clientId} focusRegime={focus?.regime} />}
      {tab === "mrm" && <AssuranceMRM clientId={clientId} />}
      {tab === "maturity" && <EvidenceMaturity clientId={clientId} />}
      {tab === "modelrisk" && <ModelRegister clientId={clientId} />}
      {tab === "twin" && <DigitalTwin clientId={clientId} />}
      {tab === "benchmark" && <BenchmarkPanel clientId={clientId} />}
      {tab === "access" && <PortalAccessManager clientId={clientId} />}
    </div>
  );
}
